import { useContext, useEffect, useRef, type FC } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { v4 as uuid4 } from "uuid";

import {
  GridPagingReports,
  GridRowReport,
  TableOptionsContext,
  TableWrapperContext,
} from "@ryde/controls";
import { useReportId } from "@ryde/redux";
import {
  isReportsTableColumnsSizedSF,
  tableElementRefReportsAtom,
  type ITableDto,
} from "@ryde/shared";

import {
  GridTableFooterCellReports,
  GridTableHeadCellReports,
  TableRowEmptyReport,
} from "./cells";
import { ReportGridWrapper, TableTagReportStyled } from "./report-grid.styled";
import { useReportColumnsSizingStore } from "./use-report-columns-sizing-store";

export const ReportGridInner: FC = () => {
  const { renderSubComponent } = useContext(TableWrapperContext);
  const { table } = useContext(TableOptionsContext);
  const { reportId } = useReportId();
  const isTableSized = useRecoilValue(isReportsTableColumnsSizedSF(reportId));
  const columnsSizingStore = useReportColumnsSizingStore();
  const [tableElementReports, setTableElementReports] = useRecoilState(
    tableElementRefReportsAtom,
  );

  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    // We need only first table for count dimensions
    if (tableElementReports) {
      return;
    }

    setTableElementReports(tableRef.current);
  }, [setTableElementReports, tableElementReports]);

  return (
    table && (
      <ReportGridWrapper>
        <TableTagReportStyled
          className="main_table"
          ref={tableRef}
          $isSized={isTableSized}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr data-e2e="table-head-row-report" key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <GridTableHeadCellReports<ITableDto>
                      key={header.id}
                      header={header}
                      isTableSized={isTableSized}
                      columnsSizingStore={columnsSizingStore}
                    />
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {!!table.getRowModel()?.rows?.length &&
              table.getRowModel().rows.map(row => {
                /* unique row id is used, because dynamic grouping or report may 
                   repeat any column value on grouping */
                const uniqueRowId = uuid4();
                return (
                  <GridRowReport
                    key={uniqueRowId}
                    row={row}
                    renderSubComponent={renderSubComponent}
                  />
                );
              })}

            {!table.getRowModel()?.rows?.length && (
              <TableRowEmptyReport
                cells={table.getHeaderGroups()[0]?.headers?.length}
              />
            )}
          </tbody>
          {!!table.getRowModel()?.rows?.length && (
            <tfoot>
              <tr
                data-e2e="table-footer-row-divider-footer"
                className="divider-footer-row"
              >
                <th
                  colSpan={table.getFooterGroups()[0]?.headers?.length || 1}
                />
              </tr>
              {table.getFooterGroups().map(footerGroup => (
                <tr data-e2e="table-footer-row-report" key={footerGroup.id}>
                  {footerGroup.headers.map(footer => {
                    return (
                      <GridTableFooterCellReports<ITableDto>
                        key={footer.id}
                        footer={footer}
                      />
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </TableTagReportStyled>
        {table.getPageCount() > 1 && <GridPagingReports table={table} />}
      </ReportGridWrapper>
    )
  );
};
